import { reportAsyncError } from "@istari-frontend/shared";
import type { V3 } from "@istari/istari-client";
import { useQueries } from "@tanstack/react-query";
import type { RefObject } from "react";
import { useEffect, useRef } from "react";

import { changeRequestQueryOptions } from "~/change-request/data-source/api/change-request-query-options";
import { userQueryOptions } from "~/user/data-source/user-query-options";

import { toNotificationActorName } from "./resolve-notification-actor-name";

export type ChangeRequestDetail = { title: string | null; createdById: string | null };

export type ChangeRequestEnrichment = {
  changeRequestDetails: ChangeRequestDetail[];
  requesterNames: (string | null)[];
};

type Failure = { key: string; error: unknown; context: Record<string, unknown> };

function reportNewFailures(failures: Failure[], reportedKeys: RefObject<Set<string>>, label: string) {
  failures.forEach((failure) => {
    if (reportedKeys.current.has(failure.key)) return;
    reportedKeys.current.add(failure.key);
    reportAsyncError(label, failure.error, failure.context);
  });
}

export function useChangeRequestEnrichment(
  events: V3.SystemChangeRequestOpenedNotificationEvent[],
): ChangeRequestEnrichment {
  const reportedChangeRequestFailures = useRef<Set<string>>(new Set());
  const reportedRequesterFailures = useRef<Set<string>>(new Set());

  // combine applies replaceEqualDeep to the result, keeping stable references when data is
  // unchanged, so callers can feed both arrays straight into a useMemo dep list. It must stay a
  // pure mapping — no side effects here, since combine re-runs on every render these queries
  // participate in, not once per failure.
  const { details: changeRequestDetails, failures: changeRequestFailures } = useQueries({
    queries: events.map((event) => changeRequestQueryOptions.byId(event.systemId, event.changeRequestId)),
    combine: (results) => ({
      details: results.map((result) =>
        result.status === "error"
          ? { title: null, createdById: null }
          : { title: result.data?.title ?? null, createdById: result.data?.createdById ?? null },
      ),
      failures: results.flatMap((result, index): Failure[] => {
        if (result.status !== "error") return [];
        const event = events[index];
        return [
          {
            key: `${event.systemId}:${event.changeRequestId}`,
            error: result.error,
            context: { systemId: event.systemId, changeRequestId: event.changeRequestId },
          },
        ];
      }),
    }),
  });

  useEffect(() => {
    reportNewFailures(
      changeRequestFailures,
      reportedChangeRequestFailures,
      "notifications-change-request-lookup-failed",
    );
  }, [changeRequestFailures]);

  const { names: requesterNames, failures: requesterFailures } = useQueries({
    queries: changeRequestDetails.map((detail) => ({
      ...userQueryOptions.byId(detail.createdById ?? ""),
      enabled: !!detail.createdById,
    })),
    combine: (results) => ({
      names: results.map((result) => (result.status === "error" ? null : toNotificationActorName(result.data))),
      failures: results.flatMap((result, index): Failure[] => {
        if (result.status !== "error") return [];
        const userId = changeRequestDetails[index].createdById;
        if (!userId) return [];
        return [{ key: userId, error: result.error, context: { userId } }];
      }),
    }),
  });

  useEffect(() => {
    reportNewFailures(
      requesterFailures,
      reportedRequesterFailures,
      "notifications-change-request-requester-lookup-failed",
    );
  }, [requesterFailures]);

  return { changeRequestDetails, requesterNames };
}
